import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import {
  addWeekdayCheckInRestriction,
  addWeekdayCheckOutRestriction,
  getWeekdayCheckInRulesAsync,
  getWeekdayCheckOutRulesAsync,
  removeWeekdayCheckInRestriction,
  removeWeekdayCheckOutRestriction,
  selectWeekdayCheckInRuleItems,
  selectWeekdayCheckInRuleItemsLoadingStatus,
  selectWeekdayCheckOutRuleItems,
  selectWeekdayCheckOutRuleItemsLoadingStatus,
  updateActiveForWeekdayCheckInRestrictionData,
  updateActiveForWeekdayCheckOutRestrictionData,
  updateCopyableForWeekdayCheckInRestrictionData,
  updateCopyableForWeekdayCheckOutRestrictionData,
  updateDefaultRateWeekdayCheckInAndCheckOutRulesAsync,
} from '../defaultRateWeekdayRulesSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import WeekdayCheckInCheckOutRestrictionsForm from './WeekdayCheckInCheckOutRestrictionsForm';
import {
  WeekdayCheckInRuleItem,
  WeekdayCheckOutRuleItem,
} from '../../../common/types/rates.types';
import { copyWeekdayCheckInAndCheckOutRulesToSeasons } from '../../defaultRateTools/defaultRateToolsSlice';
import { formatDateToFinnish } from '../../../common/utils/dateUtils';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';

function DefaultRateWeekdayCheckInCheckOutRestrictions({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const weekdayCheckInRuleItems = useAppSelector(selectWeekdayCheckInRuleItems);
  const weekdayCheckOutRuleItems = useAppSelector(
    selectWeekdayCheckOutRuleItems,
  );
  const weekdayCheckInRuleItemsStatus = useAppSelector(
    selectWeekdayCheckInRuleItemsLoadingStatus,
  );
  const weekdayCheckOutRuleItemsStatus = useAppSelector(
    selectWeekdayCheckOutRuleItemsLoadingStatus,
  );

  const [submitted, setSubmitted] = useState(false);
  const [copyStartDate, setCopyStartDate] = useState<Date | null>(null);
  const [copyEndDate, setCopyEndDate] = useState<Date | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    dispatch(getWeekdayCheckInRulesAsync(defaultRateId));
    dispatch(getWeekdayCheckOutRulesAsync(defaultRateId));
  }, [defaultRateId, dispatch]);

  const handleWeekdayCheckInRuleChange = (event, weekdayInt) => {
    const { checked } = event.target;
    if (checked) {
      dispatch(removeWeekdayCheckInRestriction(weekdayInt));
    } else {
      const newWeekdayCheckInRestriction = {} as WeekdayCheckInRuleItem;
      newWeekdayCheckInRestriction.weekday = weekdayInt;
      newWeekdayCheckInRestriction.defaultRateId = defaultRateId;
      newWeekdayCheckInRestriction.isCopyable = true;
      newWeekdayCheckInRestriction.isActive = true;

      dispatch(addWeekdayCheckInRestriction(newWeekdayCheckInRestriction));
    }
  };

  const handleWeekdayCheckOutRuleChange = (event, weekdayInt) => {
    const { checked } = event.target;
    if (checked) {
      dispatch(removeWeekdayCheckOutRestriction(weekdayInt));
    } else {
      const newWeekdayCheckOutRestriction = {} as WeekdayCheckOutRuleItem;
      newWeekdayCheckOutRestriction.weekday = weekdayInt;
      newWeekdayCheckOutRestriction.defaultRateId = defaultRateId;
      newWeekdayCheckOutRestriction.isCopyable = true;
      newWeekdayCheckOutRestriction.isActive = true;

      dispatch(addWeekdayCheckOutRestriction(newWeekdayCheckOutRestriction));
    }
  };

  const handleCopyableWeekdayCheckInChange = event => {
    const { checked } = event.target;
    dispatch(
      updateCopyableForWeekdayCheckInRestrictionData({ copyable: checked }),
    );
  };

  const handleCopyableWeekdayCheckOutChange = event => {
    const { checked } = event.target;
    dispatch(
      updateCopyableForWeekdayCheckOutRestrictionData({ copyable: checked }),
    );
  };

  const handleActiveWeekdayCheckInChange = event => {
    const { checked } = event.target;
    dispatch(updateActiveForWeekdayCheckInRestrictionData({ active: checked }));
  };

  const handleActiveWeekdayCheckOutChange = event => {
    const { checked } = event.target;
    dispatch(
      updateActiveForWeekdayCheckOutRestrictionData({ active: checked }),
    );
  };

  const onSubmit = () => {
    const rulesData = {
      defaultRateId,
      weekdayCheckInRules: weekdayCheckInRuleItems,
      weekdayCheckOutRules: weekdayCheckOutRuleItems,
    };

    dispatch(updateDefaultRateWeekdayCheckInAndCheckOutRulesAsync(rulesData));
    setSubmitted(true);
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyWeekdayCheckInAndCheckOutRulesToSeasons({
          accommodationId,
          // copyStartDate: formatDateToFinnish(copyStartDate),
          // copyEndDate: formatDateToFinnish(copyEndDate),
          selectedSeasonRateIds,
          blockedCheckInWeekdays: weekdayCheckInRuleItems,
          blockedCheckOutWeekdays: weekdayCheckOutRuleItems,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  return (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <WeekdayCheckInCheckOutRestrictionsForm
        checkInLoadingStatus={weekdayCheckInRuleItemsStatus}
        checkOutLoadingStatus={weekdayCheckOutRuleItemsStatus}
        handleCheckInInputChange={handleWeekdayCheckInRuleChange}
        handleCheckOutInputChange={handleWeekdayCheckOutRuleChange}
        handleActiveWeekdayCheckInChange={handleActiveWeekdayCheckInChange}
        handleCopyableWeekdayCheckInChange={handleCopyableWeekdayCheckInChange}
        handleActiveWeekdayCheckOutChange={handleActiveWeekdayCheckOutChange}
        handleCopyableWeekdayCheckOutChange={
          handleCopyableWeekdayCheckOutChange
        }
        onSubmit={onSubmit}
        submitted={submitted}
        weekdayCheckInRules={weekdayCheckInRuleItems}
        weekdayCheckOutRules={weekdayCheckOutRuleItems}
        hasWriteAccess={hasWriteAccess}
        isDefaultRate
      />
      <Form.Group className="mb-24">
        {/*
        <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyStartDate(date)}
            className="form-control"
            selected={copyStartDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyEndDate(date)}
            className="form-control"
            selected={copyEndDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Text muted>
          Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
          päättymispäivämäärää sekä loppupäivä on kopioinnin alkamispäivämäärän
          jälkeen.
        </Form.Text>
        */}
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateWeekdayCheckInCheckOutRestrictions;
